import { useEffect, useRef, useState, useMemo, useCallback } from 'react';
import './style.css';

import AsideNavbar from 'src/components/AsideNavbar';
import PortalNavBar from 'src/components/PortalNavbar';

import { useParams } from 'react-router-dom';

import { NewOrderStatus, OrderSellerPaymentStatus } from 'src/entities/Order';
import OrderSummary from './OrderSummary/OrderSummary';
import ProductsDetails from './ProductsDetails/ProductsDetails';
import Tracking from './Tracking/Tracking';
import Financial from './Financial/Financial';
import { OrdersService } from 'src/services/Orders';
import { useUserContext } from 'src/contexts/UserContext';
import DeliveryData from './DeliveryData/DeliveryData';
import { uploadDocument } from './utils';
import { WithChakra } from 'src/contexts/Chakra';
import ShippingDocument from './ShippingDocument/ShippingDocument';
import { Installments } from './Installments';
import { enableShippingCostValidation } from '../../utils/featureFlags';

export function NewOrderDetails() {
  const { id: uuid } = useParams();
  const { showToast, userEmail } = useUserContext();

  const detailsRef = useRef();
  const rastreioRef = useRef();
  const detailsRefReverse = useRef();

  const [loading, setLoading] = useState(true);
  const [order, setOrder] = useState(null);

  const nfRef = useRef();

  const fetchNewOrderDetails = useCallback(async () => {
    setLoading(true);
    try {
      const orderResponse = await OrdersService.getNewOrderDetails({
        uuid,
      });

      setOrder(orderResponse);
    } catch (error) {
      console.log(error);
      setOrder(null);
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    fetchNewOrderDetails();
  }, [fetchNewOrderDetails]);

  const handleUploadDocument = async ({ documentType, file }) => {
    try {
      const response = await uploadDocument({
        documentType,
        uuid,
        file,
      });

      showToast('Documento enviado com sucesso!', 'success');
      return response;
    } catch (error) {
      window.scroll(0, 0);
      showToast(
        `Erro ao enviar documento ${documentType}, Error: ${error}`,
        'error',
      );
      throw new Error(error);
    }
  };

  const orderVisibilityCondition = useMemo(() => {
    if (!order) return false;
    const status = order?.summary.orderStatus;
    return [
      NewOrderStatus.confirmed,
      NewOrderStatus.invoice_received,
      NewOrderStatus.sent,
      NewOrderStatus.shipping_confirmed,
      NewOrderStatus.delivered,
    ].includes(status);
  }, [order]);

  const canEditTracking = useMemo(() => {
    if (
      !order ||
      [
        OrderSellerPaymentStatus.CONSOLIDATED,
        OrderSellerPaymentStatus.PAID,
      ].includes(order.summary.sellerPaymentStatus)
    )
      return false;

    const status = order?.summary.orderStatus;
    return [
      NewOrderStatus.invoice_received,
      NewOrderStatus.sent,
      NewOrderStatus.shipping_confirmed,
      NewOrderStatus.delivered,
    ].includes(status);
  }, [order]);

  const canEditDeliveredData = useMemo(() => {
    if (!order) return false;

    const status = order.summary.orderStatus;

    return [
      NewOrderStatus.invoice_received,
      NewOrderStatus.sent,
      NewOrderStatus.shipping_confirmed,
      NewOrderStatus.delivered,
    ].includes(status);
  });

  const shippingCostValidationEnabled = enableShippingCostValidation(userEmail);

  return (
    <div id="wrapper" ref={detailsRefReverse}>
      <AsideNavbar />
      <div id="page-wrapper">
        <PortalNavBar />

        {loading && !order && (
          <div className="container px-3 py-4">
            <div className="row">
              <div className="col-12">
                <div className="d-flex justify-content-center">
                  <div className="spinner-border" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <WithChakra>
          {order && (
            <div className="container px-3 py-4">
              <OrderSummary
                summary={order.summary}
                ICMSST={order.shipping.ICMSST}
              />

              <Installments installments={order.installments} />

              <ProductsDetails
                detailsRef={detailsRef}
                items={order.items}
                customer={order.summary.customer}
                orderNumber={order.summary.number}
                orderUUID={order.uuid}
                orderStatus={order.summary.orderStatus}
                fetchOrderDetail={fetchNewOrderDetails}
              />

              {orderVisibilityCondition && (
                <>
                  <Financial
                    nfRef={nfRef}
                    ICMSST={order.invoice.ICMSST}
                    uploadDocument={handleUploadDocument}
                    invoice={order.invoice}
                    fetchOrderDetail={fetchNewOrderDetails}
                    orderSource={order.source}
                  />
                  <Tracking
                    canEditTracking={canEditTracking}
                    creationDate={order.summary.creationDate}
                    fetchOrderDetail={fetchNewOrderDetails}
                    orderNumber={order.summary.number}
                    orderSource={order.source}
                    orderUUID={order.uuid}
                    rastreioRef={rastreioRef}
                    shipping={order.shipping}
                    shippingCostValidationEnabled={
                      shippingCostValidationEnabled
                    }
                    subtotal={order.summary.totalPrice}
                  />
                  <ShippingDocument
                    uploadDocument={handleUploadDocument}
                    shippingDocuments={order.shipping.documents}
                    orderNumber={order.summary.number}
                    canEditTracking={canEditTracking}
                  />
                  <DeliveryData
                    uploadDocument={handleUploadDocument}
                    shippingDocuments={order.shipping.documents}
                    orderNumber={order.summary.number}
                    canEditDeliveredData={canEditDeliveredData}
                  />
                </>
              )}
            </div>
          )}
        </WithChakra>
      </div>
    </div>
  );
}
